import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  Spinner,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Toaster,
  Toast,
  ToastTitle,
  useToastController,
} from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  Home20Regular,
  VehicleCarProfile20Regular,
  Document20Regular,
  ChevronDown20Regular,
} from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import {
  matchVertragsartFromBranch,
  findVertragsart,
} from "../data/vertragskatalog";
import AddRiskToContractDialog from "../components/contractDetail/AddRiskToContractDialog";
import ContractDetailView from "../components/contractDetail/ContractDetailView";
import { useWindowManager } from "../components/windowManager/WindowManagerContext";
import { openContractFormWindow } from "../components/windowManager/ContractFormWindow";
import AllgemeinDetails, { type AllgemeinField } from "../components/contractDetail/sections/AllgemeinDetails";
import {
  immoSectionFields,
  immoRiskFields,
  type ImmoSectionKey,
} from "../components/contractDetail/sections/ImmobilieDetails";
import KfzRisikenDetails from "../components/contractDetail/sections/KfzRisikenDetails";
import KfzReadOnlyTables from "../components/contractDetail/sections/KfzReadOnlyTables";
import { TARIFIERUNGSARTEN, VERTRAGSARTEN } from "../components/kfzContractStep/data";
import type {
  ContactSummary,
  RiskSummary,
  TarifierungKey,
  VertragsartKey,
} from "../components/kfzContractStep/types";
import { getVereinbarungMock } from "../components/kfzContractStep/vereinbarungMock";
import type { VersichererKey } from "../components/kfzContractStep/VertragsBasisSection";

type ContractKind = "immobilie" | "kfz" | "generic";

type ContractLite = {
  id?: number;
  contractNbr?: string | null;
  insuranceNbr?: string | null;
  branch?: string | null;
  contactId?: number | null;
  isCollectiveContract?: boolean | null;
  beginPolicyTerm?: string | null;
  endPolicyTerm?: string | null;
  mainDueDate?: string | null;
  nextDueDate?: string | null;
  premiumAnnualGross?: number | null;
  premiumAnnualNet?: number | null;
  vatRate?: number | null;
  currency?: string | null;
  remark?: string | null;
  riskObjects?: Array<{ id?: number; riskType?: string | null }>;
  characteristics?: Array<{ nameBezeichnung?: string; contractCharacteristicValue?: string }>;
};

const isImmoBranch = (branch?: string | null): boolean => {
  const b = (branch ?? "").toLowerCase();
  return b.includes("immobil") || b.includes("wohngeb") || b.includes("gewerbegeb");
};

const isKfzBranch = (branch?: string | null): boolean => {
  const b = (branch ?? "").toLowerCase();
  return b.includes("kfz") || b.includes("kraftfahrzeug");
};

const detectKind = (contract: ContractLite | null): ContractKind => {
  if (!contract) return "generic";
  if (isImmoBranch(contract.branch)) return "immobilie";
  if (isKfzBranch(contract.branch)) return "kfz";
  const types = (contract.riskObjects ?? []).map((r) => (r.riskType ?? "").toLowerCase());
  if (types.some((t) => t.includes("immo"))) return "immobilie";
  if (types.some((t) => t.includes("kfz"))) return "kfz";
  return "generic";
};

const readCharacteristic = (c: ContractLite | null, key: string): string | null => {
  if (!c) return null;
  const hit = (c.characteristics ?? []).find((x) => x.nameBezeichnung === key);
  return hit?.contractCharacteristicValue ?? null;
};

const parseJsonChar = <T,>(c: ContractLite | null, key: string, fallback: T): T => {
  const raw = readCharacteristic(c, key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const buildDisplay = (c: any, fallbackId: number): string => {
  if (!c) return `Kontakt ${fallbackId}`;
  if (typeof c.displayName === "string" && c.displayName.trim()) return c.displayName;
  const first = c.firstName ?? "";
  const last = c.lastname ?? "";
  if (last && first) return `${last}, ${first}`;
  if (last) return String(last);
  if (first) return String(first);
  if (c.companyName) return String(c.companyName);
  return `Kontakt ${fallbackId}`;
};

const buildContactSummary = (c: any, fallbackId: number, role?: string): ContactSummary => ({
  id: c?.id ?? fallbackId,
  displayName: buildDisplay(c, fallbackId),
  role,
  street: c?.defaultAddress?.street ?? c?.address?.street,
  postalCode: c?.defaultAddress?.postalCode ?? c?.address?.postalCode,
  city: c?.defaultAddress?.city ?? c?.address?.city,
  country: c?.defaultAddress?.country ?? c?.address?.country,
});

const fetchRiskById = async (id: number): Promise<RiskSummary | null> => {
  try {
    const res = await fetch(apiUrl(`risk-objects/${id}`));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const charsArr: Array<{ nameBezeichnung?: string; riskCharacteristicValue?: string }> =
      Array.isArray(data.characteristics) ? data.characteristics : [];
    const characteristics: Record<string, string> = {};
    for (const c of charsArr) {
      if (c.nameBezeichnung) {
        characteristics[c.nameBezeichnung] = String(c.riskCharacteristicValue ?? "");
      }
    }
    const contactList: ContactSummary[] = [];
    if (data.contact && data.contact.id) {
      contactList.push(buildContactSummary(data.contact, data.contact.id, "Halter"));
    }
    return {
      id: data.id,
      name: data.name ?? "",
      characteristics,
      contacts: contactList,
    };
  } catch (e) {
    console.error("fetchRiskById failed", id, e);
    return null;
  }
};

const formatEur = (v: number | null | undefined): string | null => {
  if (v === null || v === undefined) return null;
  return `${v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
};

// Tolerates both German ("1.234,56") and plain ("1234.56") decimal input.
const parseGermanDecimal = (s: string): number | null => {
  const t = s.trim();
  if (t === "") return null;
  const normalized = t.includes(",") ? t.replace(/\./g, "").replace(",", ".") : t;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

// `<input type="date">` needs YYYY-MM-DD; trim any time component.
const toDateInput = (s: string | null | undefined): string =>
  s ? String(s).slice(0, 10) : "";

// Inline-editable Allgemein entity fields (NOT characteristics): editing these
// PUTs only the entity columns, so the characteristics array stays untouched.
const CONTRACT_NUMBER_KEYS = new Set(["premiumAnnualGross", "premiumAnnualNet"]);

const KIND_HEADER: Record<
  ContractKind,
  { label: string; icon: React.ReactNode }
> = {
  kfz: { label: "KFZ-Versicherung", icon: <VehicleCarProfile20Regular /> },
  immobilie: { label: "Immobilien-Versicherung", icon: <Home20Regular /> },
  generic: { label: "Versicherungsvertrag", icon: <Document20Regular /> },
};

const ContractDetailDispatcher = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toasterId = "contract-detail-toaster";
  const { dispatchToast } = useToastController(toasterId);
  const { openWindow } = useWindowManager();
  const [kind, setKind] = useState<ContractKind | null>(null);
  const [contract, setContract] = useState<ContractLite | null>(null);
  const [contactName, setContactName] = useState<string | null>(null);
  const [risks, setRisks] = useState<RiskSummary[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [edited, setEdited] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let aborted = false;
    const run = async () => {
      if (!id) return;
      try {
        const res = await fetch(apiUrl(`contracts/${id}`));
        if (!res.ok) {
          if (!aborted) {
            setKind("generic");
            setContract(null);
          }
          return;
        }
        const data = (await res.json()) as ContractLite;
        if (!aborted) {
          setKind(detectKind(data));
          setContract(data);
          setEdited({});
        }
      } catch {
        if (!aborted) {
          setKind("generic");
          setContract(null);
        }
      }
    };
    run();
    return () => {
      aborted = true;
    };
  }, [id, reloadKey]);

  // Kontaktname nachladen
  useEffect(() => {
    let aborted = false;
    const cid = contract?.contactId;
    if (!cid) {
      setContactName(null);
      return;
    }
    (async () => {
      try {
        const res = await fetch(apiUrl(`contact/${cid}`));
        if (!res.ok) return;
        const data = await res.json();
        if (!aborted) setContactName(buildDisplay(data, cid));
      } catch {
        // ignore
      }
    })();
    return () => {
      aborted = true;
    };
  }, [contract?.contactId]);

  // Risiko-Details nachladen (KFZ- UND Immobilien-Sektionen).
  useEffect(() => {
    let aborted = false;
    if ((kind !== "kfz" && kind !== "immobilie") || !contract?.riskObjects?.length) {
      setRisks([]);
      return;
    }
    const ids = contract.riskObjects
      .map((r) => Number(r.id))
      .filter((n) => Number.isFinite(n) && n > 0);
    (async () => {
      const loaded: RiskSummary[] = [];
      for (const rid of ids) {
        const r = await fetchRiskById(rid);
        if (r) loaded.push(r);
      }
      if (!aborted) setRisks(loaded);
    })();
    return () => {
      aborted = true;
    };
  }, [kind, contract?.riskObjects]);

  // KFZ-spezifische Characteristics deserialisieren.
  const kfzData = useMemo(() => {
    const versicherer = (readCharacteristic(contract, "kfz.versicherer") ??
      "") as VersichererKey;
    const vertragsart = (readCharacteristic(contract, "kfz.vertragsart") ??
      "") as VertragsartKey | "";
    const tarifierung = (readCharacteristic(contract, "kfz.tarifierungsart") ??
      "") as TarifierungKey | "";
    const sammelGrid = parseJsonChar(contract, "kfz.sammelgrid_json", {
      klassen: {},
    } as { klassen: Record<string, unknown> });
    const umfangAssignment = parseJsonChar<Record<number, string>>(
      contract,
      "kfz.umfang_assignment_json",
      {},
    );
    const werteByRisk = parseJsonChar<Record<number, Record<string, string>>>(
      contract,
      "kfz.werte_json",
      {},
    );
    const zahlung = parseJsonChar<Record<string, string>>(
      contract,
      "kfz.zahlung_json",
      {},
    );
    return {
      versicherer,
      vertragsart,
      tarifierung,
      sammelGrid,
      umfangAssignment,
      werteByRisk,
      zahlung,
    };
  }, [contract]);

  // Risiken die nach der Vertragserstellung hinzugefügt wurden, haben weder eine
  // Umfang-Zuordnung noch Wert-Eingaben — die markieren wir als "neu".
  const newRiskIds = useMemo(() => {
    const set = new Set<number>();
    for (const r of risks) {
      const hasAssignment = !!kfzData.umfangAssignment[r.id];
      const hasWerte = !!kfzData.werteByRisk[r.id];
      if (!hasAssignment && !hasWerte) set.add(r.id);
    }
    return set;
  }, [risks, kfzData.umfangAssignment, kfzData.werteByRisk]);

  if (kind == null) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <Spinner size="small" label="Vertrag wird geladen…" />
      </div>
    );
  }

  // Header
  const kindMeta = KIND_HEADER[kind];
  const artId = matchVertragsartFromBranch(contract?.branch ?? "");
  const kategorie = artId
    ? findVertragsart(artId)?.kat.name ?? null
    : contract?.branch ?? null;
  const vertragsartLabel =
    kind === "kfz"
      ? VERTRAGSARTEN.find((v) => v.key === kfzData.vertragsart)?.label
      : undefined;
  const produktLabel =
    kind === "kfz"
      ? TARIFIERUNGSARTEN.find((t) => t.key === kfzData.tarifierung)?.label
      : undefined;

  // Command bar
  const isCollective =
    !!contract?.isCollectiveContract ||
    kfzData.vertragsart === "sammel" ||
    kfzData.vertragsart === "buendel" ||
    kfzData.tarifierung === "rahmenSammel" ||
    kfzData.tarifierung === "rahmenStueck";

  const contractId = Number(id);
  const eligibleForAttach = isCollective && kind === "kfz" && Number.isFinite(contractId);
  const attachedRiskIds = (contract?.riskObjects ?? [])
    .map((r) => Number(r.id))
    .filter((n) => Number.isFinite(n) && n > 0);

  const comingSoon = (what: string) =>
    dispatchToast(
      <Toast>
        <ToastTitle>{what} — folgt im nächsten Schritt</ToastTitle>
      </Toast>,
      { intent: "info" },
    );

  const cmdBtnStyle: React.CSSProperties = {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    fontWeight: 700,
    padding: "8px 16px",
    borderRadius: 999,
    border: `1px solid ${BRAND.purple}`,
    display: "inline-flex",
    alignItems: "center",
    columnGap: 6,
  };

  const backButton = (
    <button
      type="button"
      style={cmdBtnStyle}
      onClick={() => navigate(-1)}
    >
      <ArrowLeft20Regular />
      Zurück
    </button>
  );

  const openContractEditWindow = () => {
    if (!id) return;
    const numericId = Number(id);
    if (!Number.isFinite(numericId) || numericId <= 0) {
      comingSoon("Bearbeiten");
      return;
    }
    if (kind === "kfz" || kind === "immobilie") {
      openContractFormWindow(openWindow, {
        variant: kind,
        contactId: contract?.contactId ?? null,
        contactName: contactName ?? null,
        contractId: numericId,
      });
      return;
    }
    comingSoon("Bearbeiten für generische Verträge");
  };

  const hasEdits = Object.keys(edited).length > 0;

  const handleSave = async () => {
    if (!contract || saving) return;
    if (!hasEdits) {
      dispatchToast(
        <Toast>
          <ToastTitle>Keine Änderungen zu speichern</ToastTitle>
        </Toast>,
        { intent: "info" },
      );
      return;
    }
    setSaving(true);
    // Only entity columns are edited inline; `characteristics` is NOT sent, so
    // the backend leaves the characteristics array untouched (structural KFZ /
    // Immobilie data is still edited via the "Vertrag bearbeiten" wizard).
    const body: Record<string, unknown> = {};
    for (const k of Object.keys(edited)) {
      const raw = edited[k];
      if (CONTRACT_NUMBER_KEYS.has(k)) {
        body[k] = raw.trim() === "" ? null : parseGermanDecimal(raw);
      } else {
        body[k] = raw.trim() === "" ? null : raw;
      }
    }
    try {
      const res = await fetch(apiUrl(`contracts/${id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        dispatchToast(
          <Toast>
            <ToastTitle>Vertrag gespeichert</ToastTitle>
          </Toast>,
          { intent: "success" },
        );
        setEdited({});
        setReloadKey((k) => k + 1);
      } else {
        dispatchToast(
          <Toast>
            <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
          </Toast>,
          { intent: "error" },
        );
      }
    } catch {
      dispatchToast(
        <Toast>
          <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  let actionBar: React.ReactNode = null;
  if (isCollective) {
    actionBar = (
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <button type="button" style={cmdBtnStyle}>
            Risiko-Aktionen
            <ChevronDown20Regular />
          </button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              disabled={!eligibleForAttach}
              onClick={() => eligibleForAttach && setAddDialogOpen(true)}
            >
              Risiko hinzufügen
            </MenuItem>
            <MenuItem onClick={() => comingSoon("Risiko ersetzen")}>
              Risiko ersetzen
            </MenuItem>
            <MenuItem onClick={() => comingSoon("Risiko löschen")}>
              Risiko löschen
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  } else {
    actionBar = (
      <button
        type="button"
        style={cmdBtnStyle}
        onClick={() => comingSoon("Risiko ersetzen")}
      >
        Risiko ersetzen
      </button>
    );
  }

  const editButton =
    kind === "kfz" || kind === "immobilie" ? (
      <button
        type="button"
        style={{
          ...cmdBtnStyle,
          backgroundColor: BRAND.purple,
          color: BRAND.white,
          borderColor: BRAND.purple,
        }}
        onClick={() => openContractEditWindow()}
      >
        Vertrag bearbeiten
      </button>
    ) : null;

  const saveButton = (
    <button
      type="button"
      style={{ ...cmdBtnStyle, opacity: saving || !hasEdits ? 0.6 : 1 }}
      onClick={handleSave}
      disabled={saving || !hasEdits}
    >
      {saving ? "Speichert…" : "Speichern"}
    </button>
  );

  const commandBar: React.ReactNode = (
    <>
      {backButton}
      {saveButton}
      {editButton}
      {actionBar}
    </>
  );

  // Section content
  // Editable entity scalars carry editKey/editValue/inputType; derived /
  // structural fields (Branche, Versicherer, Vertragsart, Produkt,
  // Sammelvertrag) stay read-only and are edited via the wizard.
  const allgemeinFields: AllgemeinField[] =
    kind === "kfz"
      ? [
          { label: "Vertragsnummer", value: contract?.contractNbr ?? null, editKey: "contractNbr", editValue: contract?.contractNbr ?? "", inputType: "text" },
          { label: "Versicherungsschein", value: contract?.insuranceNbr ?? null, editKey: "insuranceNbr", editValue: contract?.insuranceNbr ?? "", inputType: "text" },
          { label: "Branche", value: contract?.branch ?? null },
          {
            label: "Versicherer",
            value:
              kfzData.versicherer === "generali"
                ? "Generali"
                : kfzData.versicherer || null,
          },
          { label: "Vertragsart", value: vertragsartLabel ?? null },
          { label: "Produkt / Tarifierungsart", value: produktLabel ?? null },
          { label: "Sammelvertrag", value: isCollective ? "Ja" : "Nein" },
          { label: "Beginn", value: contract?.beginPolicyTerm ?? null, editKey: "beginPolicyTerm", editValue: toDateInput(contract?.beginPolicyTerm), inputType: "date" },
          { label: "Ablauf", value: contract?.endPolicyTerm ?? null, editKey: "endPolicyTerm", editValue: toDateInput(contract?.endPolicyTerm), inputType: "date" },
          { label: "Hauptfälligkeit", value: contract?.mainDueDate ?? null, editKey: "mainDueDate", editValue: toDateInput(contract?.mainDueDate), inputType: "date" },
          { label: "Nächste Fälligkeit", value: contract?.nextDueDate ?? null, editKey: "nextDueDate", editValue: toDateInput(contract?.nextDueDate), inputType: "date" },
          {
            label: "Jahresprämie (Brutto)",
            value: formatEur(contract?.premiumAnnualGross),
            editKey: "premiumAnnualGross",
            editValue: contract?.premiumAnnualGross != null ? String(contract.premiumAnnualGross) : "",
            inputType: "number",
          },
          {
            label: "Jahresprämie (Netto)",
            value: formatEur(contract?.premiumAnnualNet),
            editKey: "premiumAnnualNet",
            editValue: contract?.premiumAnnualNet != null ? String(contract.premiumAnnualNet) : "",
            inputType: "number",
          },
          { label: "Bemerkung", value: contract?.remark ?? null, editKey: "remark", editValue: contract?.remark ?? "", inputType: "text" },
        ]
      : [
          { label: "Vertragsnummer", value: contract?.contractNbr ?? null, editKey: "contractNbr", editValue: contract?.contractNbr ?? "", inputType: "text" },
          { label: "Versicherungsschein", value: contract?.insuranceNbr ?? null, editKey: "insuranceNbr", editValue: contract?.insuranceNbr ?? "", inputType: "text" },
          { label: "Branche", value: contract?.branch ?? null },
          { label: "Beginn", value: contract?.beginPolicyTerm ?? null, editKey: "beginPolicyTerm", editValue: toDateInput(contract?.beginPolicyTerm), inputType: "date" },
          { label: "Ablauf", value: contract?.endPolicyTerm ?? null, editKey: "endPolicyTerm", editValue: toDateInput(contract?.endPolicyTerm), inputType: "date" },
          { label: "Hauptfälligkeit", value: contract?.mainDueDate ?? null, editKey: "mainDueDate", editValue: toDateInput(contract?.mainDueDate), inputType: "date" },
          {
            label: "Jahresprämie (Brutto)",
            value: formatEur(contract?.premiumAnnualGross),
            editKey: "premiumAnnualGross",
            editValue: contract?.premiumAnnualGross != null ? String(contract.premiumAnnualGross) : "",
            inputType: "number",
          },
          {
            label: "Jahresprämie (Netto)",
            value: formatEur(contract?.premiumAnnualNet),
            editKey: "premiumAnnualNet",
            editValue: contract?.premiumAnnualNet != null ? String(contract.premiumAnnualNet) : "",
            inputType: "number",
          },
          { label: "Bemerkung", value: contract?.remark ?? null, editKey: "remark", editValue: contract?.remark ?? "", inputType: "text" },
        ];

  const vereinbarung = kind === "kfz"
    ? getVereinbarungMock(kfzData.versicherer, kfzData.tarifierung)
    : null;

  const renderSection = (section: { key: string }) => {
    if (section.key === "allgemein") {
      return (
        <AllgemeinDetails
          fields={allgemeinFields}
          values={edited}
          onChange={(k, v) => setEdited((prev) => ({ ...prev, [k]: v }))}
        />
      );
    }
    // Immobilie: die bisher leeren Sektionen aus den gespeicherten Merkmalen +
    // Prämien-/Zahlungs-Skalaren read-only befüllen (sonst „Daten folgen…").
    if (kind === "immobilie") {
      if (section.key === "risiken") {
        const rf = immoRiskFields(risks);
        return rf.length ? <AllgemeinDetails fields={rf} /> : null;
      }
      if (
        section.key === "vereinbarung" ||
        section.key === "umfang" ||
        section.key === "beitraege"
      ) {
        const f = immoSectionFields(section.key as ImmoSectionKey, contract);
        return f.length ? <AllgemeinDetails fields={f} /> : null;
      }
    }
    if (kind === "kfz") {
      if (section.key === "risiken") {
        return <KfzRisikenDetails risks={risks} newRiskIds={newRiskIds} />;
      }
      if (
        section.key === "summen" ||
        section.key === "vereinbarung" ||
        section.key === "umfang" ||
        section.key === "beitraege"
      ) {
        return (
          <KfzReadOnlyTables
            sectionKey={section.key as "summen" | "vereinbarung" | "umfang" | "beitraege"}
            tarifierung={kfzData.tarifierung}
            vereinbarung={vereinbarung}
            sammelGrid={kfzData.sammelGrid as unknown as import("../components/kfzContractStep/SammelvertragGrid").SammelGridState}
            risks={risks}
            werteByRisk={kfzData.werteByRisk}
            umfangAssignment={kfzData.umfangAssignment}
            zahlung={kfzData.zahlung}
          />
        );
      }
    }
    return null;
  };

  return (
    <>
      <Toaster toasterId={toasterId} />
      <ContractDetailView
        header={{
          kindLabel: kindMeta.label,
          kindIcon: kindMeta.icon,
          kategorie: kategorie ?? undefined,
          contractNbr: contract?.contractNbr ?? undefined,
          contractIdFallback: id,
          contactName: contactName ?? undefined,
          vertragsartLabel,
          produktLabel,
        }}
        commandBar={commandBar}
        renderSection={renderSection}
      />
      {eligibleForAttach && (
        <AddRiskToContractDialog
          open={addDialogOpen}
          contractId={contractId}
          contactId={contract?.contactId ?? null}
          alreadyAttachedRiskIds={attachedRiskIds}
          onClose={() => setAddDialogOpen(false)}
          onAttached={() => setReloadKey((k) => k + 1)}
        />
      )}
    </>
  );
};

export default ContractDetailDispatcher;
